import * as React from 'react';
import { useHistory } from 'react-router';
import { SketchPicker } from 'react-color';
import { ApolloError } from '@apollo/client';
import {
  useClonePlaceMutation,
  useDeletePlaceEnclosingSvgsMutation,
  useDeletePlaceSymbolSvgsListMutation,
  useGetAccountsQuery,
  useGetCustomersQuery,
  useGetPlaceEnclosingSvgsLazyQuery,
  useGetPlaceSymbolSvgsLazyQuery,
  useGetPlaceLazyQuery,
  useGetPlaceNamesLazyQuery,
  useUpdatePlaceEnclosingSvgsMutation,
  useUpdatePlaceSymbolSvgsMutation,
  useUpdatePlaceSymbolSvgsListMutation,
  useUpdatePlaceMutation,
} from '../generated/graphql';
import {
  SvgNameInputDialog,
  SvgDeleteConfirmDialog,
  NotifyDialog,
} from '../components/Dialogs';
import {
  GraphQLErrorDialog,
  GraphQLMutationErrorDialog,
} from '../components/Dialogs';
import { Loading } from '../Loading';
import './PlaceCreatePage.css';
import { useValidation } from '../utils/Validation';
import { ValidationRules } from '../utils/ValidationRules';
import { Input, Checkbox } from '../components/Input';
import { Validated } from '../components/Validated';
import { Button, UpdateButton } from '../components/Button';
import { PlaceTag } from '../components/PlaceTag';
import { ReadFilesToStringAsync, ReadFileToStringAsync } from '../utils/ReadFileToString';

interface PlaceCreatePageProps {
  existingId?: number;
}

interface Account {
  id: number;
  name: string;
  screen_id: string;
}

export const PlaceCreatePage: React.FC<PlaceCreatePageProps> = function (
  props
) {
  const history = useHistory();
  const validation = useValidation(ValidationRules.Place);

  const [copy, setCopy] = React.useState(false);
  const [copySrc, setCopySrc] = React.useState<number | null>(null);
  const [copyCodes, setCopyCodes] = React.useState(false);
  const [copySorts, setCopySorts] = React.useState(false);
  const [copyFaces, setCopyFaces] = React.useState(false);

  const [name, setName] = validation.useState('name', '');
  const [chiefName, setChiefName] = validation.useState('chiefName', '');
  const [customerId, setCustomerId] = validation.useState('customerId', 0);
  const [pictureLayerColor, setPictureLayerColor] = React.useState<string>('#FFFFFF');
  const [accounts, setAccounts] = React.useState<Account[]>([]);
  const [lv1Accounts, setLv1Accounts] = React.useState<Account[]>([]);
  const [selectedAccountId, setSelectedAccountId] = React.useState<number>();
  const [selectedTagName, setSelectedTagName] = React.useState<string>('');
  const [tags, setTags] = React.useState<string[]>([]);

  // SVG管理用のstate
  interface SvgItem {
    id: number;
    svg: string;
  }
  const [existingSvgs, setExistingSvgs] = React.useState<SvgItem[]>([]);
  const [pendingSvgs, setPendingSvgs] = React.useState<string[]>([]);
  const [deletedSvgIds, setDeletedSvgIds] = React.useState<number[]>([]);
  const [confirmDeleteSvgId, setConfirmDeleteSvgId] = React.useState<number | null>(null);
  const [confirmDeletePendingIndex, setConfirmDeletePendingIndex] = React.useState<number | null>(null);

  // SymbolSVG管理用のstate
  interface SymbolSvgItem {
    id: number;
    place_id: number | null;
    name: string;
    svg: string;
  }
  const [existingSymbolSvgs, setExistingSymbolSvgs] = React.useState<SymbolSvgItem[]>([]);
  const [pendingSymbolSvgs, setPendingSymbolSvgs] = React.useState<{ name: string; svg: string }[]>([]);
  const [deletedSymbolSvgIds, setDeletedSymbolSvgIds] = React.useState<number[]>([]);
  const [confirmDeleteSymbolSvgId, setConfirmDeleteSymbolSvgId] = React.useState<number | null>(null);
  const [confirmDeletePendingSymbolIndex, setConfirmDeletePendingSymbolIndex] = React.useState<number | null>(null);

  // 名前入力ダイアログ用のstate
  const [showSymbolSvgNameDialog, setShowSymbolSvgNameDialog] = React.useState(false);
  const [tempSymbolSvgItems, setTempSymbolSvgItems] = React.useState<{ fileName: string; svg: string }[]>([]);

  // 名前編集ダイアログ用のstate
  const [editingSymbolSvgId, setEditingSymbolSvgId] = React.useState<number | null>(null);
  const [editingPendingSymbolIndex, setEditingPendingSymbolIndex] = React.useState<number | null>(null);

  // デフォルトシンボル警告ダイアログ用のstate
  const [showDefaultSymbolWarning, setShowDefaultSymbolWarning] = React.useState(false);

  // クライアント未選択警告ダイアログ用のstate
  const [showCustomerSelectWarning, setShowCustomerSelectWarning] = React.useState(false);

  // 削除確認ダイアログのレンダリングヘルパー
  const renderDeleteConfirmDialog = (
    confirmId: number | null,
    getTarget: () => { svg: string; name?: string } | undefined,
    onConfirm: () => void,
    onCancel: () => void,
  ): React.ReactNode => {
    if (confirmId === null) return null;
    const target = getTarget();
    if (!target) return null;
    return (
      <SvgDeleteConfirmDialog
        open={true}
        svg={target.svg}
        name={target.name}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    );
  };

  const customerListResult = useGetCustomersQuery();
  const accountListResult = useGetAccountsQuery({
    onCompleted(data) {
      const lv1Accounts: Account[] = data.getAccounts.filter(
        (a) => a.authority.level < 2
      );
      setLv1Accounts(lv1Accounts);
      if (lv1Accounts.length > 0) {
        setSelectedAccountId(lv1Accounts[0].id);
      }
    },
  });
  const [getPlace, getPlaceResult] = useGetPlaceLazyQuery({
    onCompleted(data) {
      const place = data.getPlace;
      setName(place.name);
      setChiefName(place.chief_name);
      setCustomerId(place.client_id);
      if (data.getPlace.picture_layer_color) {
        setPictureLayerColor(data.getPlace.picture_layer_color);
      }
      setAccounts(place.authorized_workers);
      setTags(place.tags);
    },
  });
  const [getPlaceNames, getPlaceNamesResult] = useGetPlaceNamesLazyQuery({
    onCompleted(data) {
      if (data.getPlaces.length === 0) {
        return;
      }
      setCopySrc(data.getPlaces[0].id);
    },
  });

  // SVG関連のクエリ・ミューテーション
  const [getPlaceEnclosingSvgs, getPlaceEnclosingSvgsResult] = useGetPlaceEnclosingSvgsLazyQuery({
    onCompleted(data) {
      setExistingSvgs(data.getPlaceEnclosingSvgs.map(s => ({ id: s.id, svg: s.svg })));
    },
  });
  const [addEnclosingSvgs] = useUpdatePlaceEnclosingSvgsMutation();
  const [deleteEnclosingSvgs] = useDeletePlaceEnclosingSvgsMutation();

  // SymbolSVG関連のクエリ・ミューテーション
  const [getPlaceSymbolSvgs, getPlaceSymbolSvgsResult] = useGetPlaceSymbolSvgsLazyQuery({
    onCompleted(data) {
      setExistingSymbolSvgs(data.getPlaceSymbolSvgs.map(s => ({ id: s.id, place_id: s.place_id ?? null, name: s.name, svg: s.svg })));
    },
  });
  const [addSymbolSvg] = useUpdatePlaceSymbolSvgsMutation();
  const [addSymbolSvgs] = useUpdatePlaceSymbolSvgsListMutation();
  const [deleteSymbolSvgs] = useDeletePlaceSymbolSvgsListMutation();

  const [fetching, setFetching] = React.useState(false);
  const [completed, setCompleted] = React.useState(false);
  const [error, setError] = React.useState<ApolloError>();
  const [addPlace] = useUpdatePlaceMutation();
  const [clonePlace] = useClonePlaceMutation();

  if (props.existingId !== undefined) {
    if (!getPlaceResult.called) {
      getPlace({
        variables: {
          placeId: props.existingId,
        },
      });
      getPlaceEnclosingSvgs({
        variables: {
          placeId: props.existingId,
        },
      });
      getPlaceSymbolSvgs({
        variables: {
          placeId: props.existingId,
        },
      });
    }

    if (getPlaceResult.error) {
      return <GraphQLErrorDialog error={getPlaceResult.error} />;
    }

    if (getPlaceResult.loading || !getPlaceResult.data) {
      return <Loading />;
    }
  }

  if (accountListResult.loading || !accountListResult.data) {
    return <Loading />;
  }
  if (customerListResult.loading || !customerListResult.data) {
    return <Loading />;
  }

  // 作業者アカウントの一覧

  if (error) {
    return (
      <GraphQLMutationErrorDialog
        error={error}
        onOk={() => {
          setCompleted(false);
          setError(undefined);
        }}
      />
    );
  }

  if (fetching) {
    return <Loading />;
  }

  if (completed) {
    if (props.existingId) {
      history.push('/places/' + props.existingId);
    } else {
      history.push('/places');
    }
  }

  function isInvalid() {
    if (copy) {
      return validation.isInvalidProperty('name');
    }

    return validation.isInvalid;
  }

  return (
    <div className="PlaceCreatePage-container">
      <Loading
        open={getPlaceNamesResult.called && getPlaceNamesResult.loading}
      />
      <h1>{props.existingId ? '物件情報変更' : '物件新規作成'}</h1>
      {(function () {
        if (props.existingId) {
          return;
        }

        return (
          <div>
            <Checkbox
              value={copy}
              onChange={(v) => {
                if (!getPlaceNamesResult.called) {
                  getPlaceNames();
                }
                setCopy(v);
              }}
            />
            <label>物件情報をコピーする</label>
          </div>
        );
      })()}
      <div className="PlaceCreatePage-form">
        <div className="App-input_container PlaceCreatePage-input_container">
          <label className="App-label">物件名</label>
          <Input
            className="PlaceCreatePage-place_name_input"
            type="string"
            value={name}
            validationMessage={validation.get('name')}
            onChange={(event) => {
              const v = event.target.value;
              setName(() => v);
            }}
          />
        </div>
        {(function () {
          if (copy) {
            if (
              getPlaceNamesResult.loading ||
              !getPlaceNamesResult.data ||
              !copySrc
            ) {
              return;
            }

            if (getPlaceNamesResult.data.getPlaces.length === 0) {
              return <p>物件が登録されていません。</p>;
            }

            return (
              <div>
                <div className="PlaceCreatePage-input_container">
                  <select
                    value={copySrc}
                    onChange={(event) =>
                      setCopySrc(parseInt(event.target.value))
                    }
                  >
                    {getPlaceNamesResult.data.getPlaces.map((p) => (
                      <option value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="PlaceCreatePage-input_container">
                  <Checkbox
                    value={copyCodes}
                    onChange={(v) => setCopyCodes(v)}
                  />
                  <label>補修コード</label>
                </div>
                <div className="PlaceCreatePage-input_container">
                  <Checkbox
                    value={copySorts}
                    onChange={(v) => setCopySorts(v)}
                  />
                  <label>補修種別</label>
                </div>
                <div className="PlaceCreatePage-input_container">
                  <Checkbox
                    value={copyFaces}
                    onChange={(v) => setCopyFaces(v)}
                  />
                  <label>図面</label>
                </div>
              </div>
            );
          }

          return (
            <div>
              <div className="App-input_container PlaceCreatePage-input_container">
                <label className="App-label">責任者名</label>
                <Input
                  className="PlaceCreatePage-place_chief_name_input"
                  type="string"
                  value={chiefName}
                  validationMessage={validation.get('chiefName')}
                  onChange={(event) => {
                    const v = event.target.value;
                    setChiefName(() => v);
                  }}
                />
              </div>
              <div className="App-input_container PlaceCreatePage-input_container">
                <label className="App-label">クライアント</label>
                <Validated message={validation.get('customerId')}>
                  <select
                    className="PlaceCreatePage-client_select"
                    value={customerId}
                    onChange={(event) => {
                      const v = parseInt(event.target.value);
                      setCustomerId(() => v);
                    }}
                  >
                    {customerId === 0 && <option value="0">-</option>}
                    {customerListResult.data.getCustomers.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </Validated>
              </div>
              <div className='App-input_container PlaceCreatePage-input_container'>
                <label className="App-label">写真レイヤの画層色</label>
                <SketchPicker
                  color={pictureLayerColor}
                  className="PlaceCreatePage-color_picker"
                  onChange={(color) => setPictureLayerColor(color.hex)}
                />
              </div>
              <div className="App-input_container PlaceCreatePage-input_container">
                <label className="App-label">作業者リスト</label>
                <table>
                  <thead></thead>
                  <tbody>
                    {accounts.map((account) => {
                      return (
                        <tr key={account.id}>
                          <td>
                            {account.name} ({account.screen_id})
                          </td>
                          <td className="App-table_button_cell">
                            <Button
                              color="red"
                              mini={true}
                              onClick={() => {
                                setAccounts(
                                  accounts.filter((a) => a.id !== account.id)
                                );

                                // 削除されたので次の初期選択状態を更新する
                                const notSelectedLv1Account = lv1Accounts.find(a1 => a1.id === account.id || accounts.findIndex(a2 => a1.id === a2.id) === -1);
                                if (!notSelectedLv1Account) {
                                  throw new Error("必ず一つ以上のアカウントが候補に存在する");
                                }

                                setSelectedAccountId(notSelectedLv1Account.id);
                              }}
                            >
                              削除
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {(function () {
                      // まだ作業者に含まれていないアカウントリスト
                      const availableAccounts = lv1Accounts
                        .filter(
                          (lv1) =>
                            accounts.findIndex((a) => a.id === lv1.id) ===
                            -1
                        );

                      // もう選べるアカウントが存在しないなら何も表示しない
                      if (availableAccounts.length === 0) {
                        return;
                      }

                      return (
                        <tr>
                          <td>
                            <select
                              className="PlaceCreatePage-client_select"
                              value={selectedAccountId}
                              onChange={(event) => {
                                setSelectedAccountId(parseInt(event.target.value));
                              }}
                            >
                              {availableAccounts.map((a) => (
                                <option key={a.id} value={a.id}>
                                  {a.name} ({a.screen_id})
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="App-table_button_cell">
                            <Button
                              mini={true}
                              onClick={() => {
                                if (!selectedAccountId) {
                                  return;
                                }

                                // すでに登録されているアカウントは追加されないようにする
                                if (accounts.findIndex(a => a.id === selectedAccountId) !== -1) {
                                  return;
                                }
                                const selectedAccount = lv1Accounts.find(
                                  (a) => a.id === selectedAccountId
                                );
                                if (!selectedAccount) {
                                  return;
                                }

                                // 追加されたアカウントをリストに追加
                                setAccounts(accounts.concat([selectedAccount]));

                                // 作業者リストの中からまだ権限追加されていないアカウントを探して初期選択されている状態にする
                                const notSelectedLv1Account = lv1Accounts.find(a1 => a1.id !== selectedAccountId && accounts.findIndex(a2 => a1.id === a2.id) === -1);
                                if (notSelectedLv1Account) {
                                  setSelectedAccountId(notSelectedLv1Account.id);
                                } else {
                                  setSelectedAccountId(undefined);
                                }
                              }}
                            >
                              追加
                            </Button>
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
              {(function () {
                if (!getPlaceResult.data) {
                  return;
                }

                return (
                  <div className="App-input_container PlaceCreatePage-input_container">
                    <label className="App-label">ユーザータグ</label>
                    <div>
                      {(function () {
                        if (tags.length === 0) {
                          return <p>タグは登録されていません</p>
                        }
                        return (
                          <div className='PlaceCreatePage-place_tag_container'>
                            {tags.map(tag =>
                              <PlaceTag
                                onDeleteClick={() => {
                                  setTags(tags.filter(t => t !== tag));
                                }}
                              >
                                {tag}
                              </PlaceTag>)}
                          </div>
                        );
                      })()}
                      <div>
                        <Input
                          className="PlaceCreatePage-place_tag_input"
                          type="string"
                          value={selectedTagName}
                          onChange={(event) => setSelectedTagName(event.target.value)}
                        />
                        <Button
                          mini
                          className='PlaceCreatePage-place_tag_button'
                          onClick={() => {
                            setTags(tags.concat([selectedTagName]));
                            setSelectedTagName('');
                          }}>
                          追加
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {(function () {
                if (!getPlaceResult.data) {
                  return;
                }

                // 表示用のSymbolSVG一覧（既存のうち削除されていないもの + 新規追加予定）
                // デフォルトシンボル(place_id === null)を先頭に、それ以外を後ろに表示
                const displayExistingSymbolSvgs = existingSymbolSvgs
                  .filter(s => !deletedSymbolSvgIds.includes(s.id))
                  .sort((a, b) => {
                    if (a.place_id === null && b.place_id !== null) return -1;
                    if (a.place_id !== null && b.place_id === null) return 1;
                    return 0;
                  });

                return (
                  <div className="App-input_container PlaceCreatePage-input_container">
                    <label className="App-label">
                      追加シンボル図形
                    </label>
                    <label>.svgファイルをアップロードすることで、物件固有のシンボル図形を追加することができます。</label>
                    <div className='PlaceCreatePage-SvgList'>
                      <label className='PlaceCreatePage-SvgAddButton'
                        onDragOver={(e) => {
                          // これが無いと drop が発火しないことが多い
                          e.preventDefault();
                          e.stopPropagation();

                          // "copy" にすると「コピーでドロップ」表示になりやすい
                          e.dataTransfer.dropEffect = "copy";
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const files = e.dataTransfer.files;
                          if (files) {
                            ReadFilesToStringAsync(files, "image/svg+xml").then(readData => {
                              const validData = readData.filter(data => !data.error).map(data => ({ fileName: data.fileName, svg: data.content }));
                              if (validData.length > 0) {
                                setTempSymbolSvgItems(validData);
                                setShowSymbolSvgNameDialog(true);
                              }
                            });
                          }
                        }}>
                        <input
                          className='PlaceCreatePage-SvgAddButton'
                          type="file"
                          accept='.svg'
                          multiple
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const files = e.target.files;
                            if (files) {
                              ReadFilesToStringAsync(files, "image/svg+xml").then(readData => {
                                const validData = readData.filter(data => !data.error).map(data => ({ fileName: data.fileName, svg: data.content }));
                                if (validData.length > 0) {
                                  setTempSymbolSvgItems(validData);
                                  setShowSymbolSvgNameDialog(true);
                                }
                              });
                            }
                            e.target.value = '';
                          }}
                        />
                        <div className='PlaceCreatePage-SvgAddButtonLabelContainer'>
                          <div className='PlaceCreatePage-SvgAddButtonLabel' style={{ fontSize: 15 }}>ここにsvgファイルをドラッグ＆ドロップ</div>
                          <div className='PlaceCreatePage-SvgAddButtonLabel' style={{ fontSize: 12 }}>クリックでファイルを選択</div>
                        </div>
                      </label>
                      <div style={{ height: "8px" }} />
                      {(displayExistingSymbolSvgs.length > 0 || pendingSymbolSvgs.length > 0) && (
                        <div className='PlaceCreatePage-SvgListContainer'>
                          {/* 既存のSymbolSVG */}
                          {displayExistingSymbolSvgs.map(svgItem => {
                            const isDefault = svgItem.place_id === null;
                            return (
                              <div key={svgItem.id} className='PlaceCreatePage-SvgItemWrapper'>
                                <div
                                  className={`PlaceCreatePage-SvgContainer${isDefault ? '' : ' PlaceCreatePage-SvgClickable'}`}
                                  onClick={isDefault ? () => setShowDefaultSymbolWarning(true) : () => setEditingSymbolSvgId(svgItem.id)}
                                  title={isDefault ? 'デフォルトシンボルは編集・削除できません' : 'クリックして名前を編集'}
                                  style={isDefault ? { opacity: 0.6 } : undefined}
                                >
                                  <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgItem.svg).replace(/'/g, "%27").replace(/"/g, "%22")}`} />
                                </div>
                                <button
                                  className='PlaceCreatePage-SvgDeleteButton'
                                  type="button"
                                  onClick={isDefault ? () => setShowDefaultSymbolWarning(true) : () => setConfirmDeleteSymbolSvgId(svgItem.id)}
                                  style={isDefault ? { backgroundColor: '#ccc', cursor: 'not-allowed' } : undefined}
                                >
                                  削除
                                </button>
                              </div>
                            );
                          })}
                          {/* 新規追加予定のSymbolSVG */}
                          {pendingSymbolSvgs.map((svgItem, index) => {
                            return (
                              <div key={`pending-symbol-${index}`} className='PlaceCreatePage-SvgItemWrapper'>
                                <div
                                  className='PlaceCreatePage-SvgContainer PlaceCreatePage-SvgPending PlaceCreatePage-SvgClickable'
                                  onClick={() => setEditingPendingSymbolIndex(index)}
                                  title="クリックして名前を編集"
                                >
                                  <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgItem.svg).replace(/'/g, "%27").replace(/"/g, "%22")}`} />
                                </div>
                                <button
                                  className='PlaceCreatePage-SvgDeleteButton'
                                  type="button"
                                  onClick={() => setConfirmDeletePendingSymbolIndex(index)}
                                >
                                  削除
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              {(function () {
                if (!getPlaceResult.data) {
                  return;
                }

                // 表示用のSVG一覧（既存のうち削除されていないもの + 新規追加予定）
                const displayExistingSvgs = existingSvgs.filter(s => !deletedSvgIds.includes(s.id));

                return (
                  <div className="App-input_container PlaceCreatePage-input_container">
                    <label className="App-label">
                      追加囲み図形
                    </label>
                    <label>.svgファイルをアップロードすることで、物件固有の囲み図形を追加することができます。</label>
                    <div className='PlaceCreatePage-SvgList'>
                      <label className='PlaceCreatePage-SvgAddButton'
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.dataTransfer.dropEffect = "copy";
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const files = e.dataTransfer.files;
                          if (files) {
                            ReadFilesToStringAsync(files, "image/svg+xml").then(readData => {
                              setPendingSvgs([...pendingSvgs, ...readData.filter(data => !data.error).map(data => data.content)]);
                            });
                          }
                        }}>
                        <input
                          type="file"
                          accept='.svg'
                          multiple
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const files = e.target.files;
                            if (files) {
                              ReadFilesToStringAsync(files, "image/svg+xml").then(readData => {
                                setPendingSvgs([...pendingSvgs, ...readData.filter(data => !data.error).map(data => data.content)]);
                              });
                            }
                            e.target.value = '';
                          }}
                        />
                        <div className='PlaceCreatePage-SvgAddButtonLabelContainer'>
                          <div className='PlaceCreatePage-SvgAddButtonLabel' style={{ fontSize: 15 }}>ここにsvgファイルをドラッグ＆ドロップ</div>
                          <div className='PlaceCreatePage-SvgAddButtonLabel' style={{ fontSize: 12 }}>クリックでファイルを選択</div>
                        </div>
                      </label>
                      <div style={{ height: "8px" }} />
                      {(displayExistingSvgs.length > 0 || pendingSvgs.length > 0) && (
                        <div className='PlaceCreatePage-SvgListContainer'>
                          {/* 既存のSVG */}
                          {displayExistingSvgs.map(svgItem => {
                            const encodedSvg = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgItem.svg).replace(/'/g, "%27").replace(/"/g, "%22")}`;
                            return (
                              <div key={svgItem.id} className='PlaceCreatePage-SvgItemWrapper'>
                                <div className='PlaceCreatePage-SvgContainer'>
                                  <img src={encodedSvg} alt="SVG" />
                                </div>
                                <button
                                  className='PlaceCreatePage-SvgDeleteButton'
                                  type="button"
                                  onClick={() => setConfirmDeleteSvgId(svgItem.id)}
                                >
                                  <label>削除</label>
                                </button>
                              </div>
                            );
                          })}
                          {/* 新規追加予定のSVG */}
                          {pendingSvgs.map((svg, index) => {
                            const encodedSvg = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg).replace(/'/g, "%27").replace(/"/g, "%22")}`;
                            return (
                              <div key={`pending-${index}`} className='PlaceCreatePage-SvgItemWrapper'>
                                <div className='PlaceCreatePage-SvgContainer PlaceCreatePage-SvgPending'>
                                  <img src={encodedSvg} alt="SVG" />
                                </div>
                                <button
                                  className='PlaceCreatePage-SvgDeleteButton'
                                  type="button"
                                  onClick={() => setConfirmDeletePendingIndex(index)}
                                >
                                  <label>削除</label>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}
        <div className="PlaceCreatePage-button_container">
          <UpdateButton
            create={!props.existingId}
            disabled={isInvalid()}
            className="App-button PlaceCreatePage-create_button"
            onClick={async () => {
              // コピーでない場合はクライアント未選択をチェック
              if (!copy && customerId === 0) {
                setShowCustomerSelectWarning(true);
                return;
              }

              // デフォルトのSymbolSVG
              const defaultSymbols = [
                // イ-1
                { name: "イ-1", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M1 .707L.707 1H.293L0 .707V.293L.293 0H.707L1 .293z" fill="none" stroke="#000" stroke-width=".02" stroke-linejoin="round"/></svg>` },
                // タイル爆裂
                { name: "タイル爆裂", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M1 .707L.707 1H.293L0 .707V.293L.293 0H.707L1 .293zM.193.1H.807M.073.22H.927M.073.78H.927M.193.9H.807" fill="none" stroke="#000" stroke-width=".02" stroke-linejoin="round"/></svg>` },
                // イ-2
                { name: "イ-2", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M.5 0L1 .5.5 1 0 .5z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
                // ロ
                { name: "ロ", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M0 0H1V1H0zM.5 0L1 .5.5 1 0 .5z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
                // タイルはつり
                { name: "タイルはつり", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M0 0H1V1H0zM.5 0L1 .5.5 1 0 .5zM.5-.075L1.075.5.5 1.075-.075.5z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
                // ハ-1
                { name: "ハ-1", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M0 0H1V1H0z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
                // ハ-2
                { name: "ハ-2", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M.5.067L1 .933H0z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
                // ハ-3
                { name: "ハ-3", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M.5.933L1 .067H0z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
                // タイル浮き（一般部）
                { name: "タイル浮き（一般部）", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M0 0H1V1H0zM.075.075H.925V.925H.075z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
                // タイル浮き（指定部）
                { name: "タイル浮き（指定部）", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M.5.067L1 .933H0zM.5.217L.87.858H.13z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
                // タイル浮き（狭幅部）
                { name: "タイル浮き（狭幅部）", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path d="M.5.933L1 .067H0zM.5.783L.87.142H.13z" fill="none" stroke="#000" stroke-width=".02"/></svg>` },
                // ホ
                { name: "ホ", svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><circle cx=".5" cy=".5" r=".5" fill="none" stroke="#000" stroke-width=".02"/></svg>` }
              ];

              if (copy) {
                if (!copySrc) {
                  throw new Error('copySrc is null');
                }

                setFetching(true);

                try {
                  // Placeをコピー（SVGもバックエンド側でコピーされる）
                  const result = await clonePlace({
                    variables: {
                      input: {
                        place_id: copySrc,
                        name,
                        codes: copyCodes,
                        sorts: copySorts,
                        faces: copyFaces,
                      },
                    },
                  });

                  setFetching(false);
                  setCompleted(true);
                } catch (e) {
                  setFetching(false);
                  setError(e as ApolloError);
                }
                return;
              }

              setFetching(true);

              // 削除対象のSVGを削除
              if (deletedSvgIds.length > 0) {
                await deleteEnclosingSvgs({ variables: { ids: deletedSvgIds } });
              }

              // 新規SVGを追加
              if (props.existingId && pendingSvgs.length > 0) {
                await addEnclosingSvgs({
                  variables: {
                    inputs: pendingSvgs.map(svg => ({
                      place_id: props.existingId!,
                      svg,
                    })),
                  },
                });
              }

              // 削除対象のSymbolSVGを削除
              if (deletedSymbolSvgIds.length > 0) {
                await deleteSymbolSvgs({ variables: { ids: deletedSymbolSvgIds } });
              }

              // 新規SymbolSVGを追加
              if (props.existingId && pendingSymbolSvgs.length > 0) {
                await addSymbolSvgs({
                  variables: {
                    inputs: pendingSymbolSvgs.map(svgItem => ({
                      place_id: props.existingId!,
                      name: svgItem.name,
                      svg: svgItem.svg,
                    })),
                  },
                });
              }

              try {
                const result = await addPlace({
                  variables: {
                    input: {
                      id: props.existingId,
                      name: name,
                      chief_name: chiefName,
                      customer_id: customerId,
                      picture_layer_color: pictureLayerColor,
                      authorized_worker_ids: accounts.map((a) => a.id),
                      tags: tags
                    },
                  },
                });

                // 新規作成の場合はデフォルトのSVGを追加
                if (!props.existingId && result.data?.updatePlace.id) {
                  const newPlaceId = result.data.updatePlace.id;
                  await addEnclosingSvgs({
                    variables: {
                      inputs: defaultSymbols.map(defaultSymbol => ({ place_id: newPlaceId, svg: defaultSymbol.svg }))
                    },
                  });
                }

                setFetching(false);
                setCompleted(true);
              } catch (e) {
                setFetching(false);
                setError(e as ApolloError);
              }
            }}
          />
        </div>
      </div>

      {/* シンボルSVG名前入力ダイアログ */}
      <SvgNameInputDialog
        open={showSymbolSvgNameDialog}
        svgItems={tempSymbolSvgItems}
        onConfirm={(items) => {
          setPendingSymbolSvgs([...pendingSymbolSvgs, ...items]);
          setShowSymbolSvgNameDialog(false);
          setTempSymbolSvgItems([]);
        }}
        onCancel={() => {
          setShowSymbolSvgNameDialog(false);
          setTempSymbolSvgItems([]);
        }}
      />

      {/* SVG削除確認ダイアログ群 */}
      {renderDeleteConfirmDialog(
        confirmDeleteSymbolSvgId,
        () => existingSymbolSvgs.find(s => s.id === confirmDeleteSymbolSvgId),
        () => {
          setDeletedSymbolSvgIds([...deletedSymbolSvgIds, confirmDeleteSymbolSvgId!]);
          setConfirmDeleteSymbolSvgId(null);
        },
        () => setConfirmDeleteSymbolSvgId(null),
      )}
      {renderDeleteConfirmDialog(
        confirmDeletePendingSymbolIndex,
        () => pendingSymbolSvgs[confirmDeletePendingSymbolIndex!],
        () => {
          setPendingSymbolSvgs(pendingSymbolSvgs.filter((_, i) => i !== confirmDeletePendingSymbolIndex));
          setConfirmDeletePendingSymbolIndex(null);
        },
        () => setConfirmDeletePendingSymbolIndex(null),
      )}
      {renderDeleteConfirmDialog(
        confirmDeleteSvgId,
        () => {
          const item = existingSvgs.find(s => s.id === confirmDeleteSvgId);
          return item ? { svg: item.svg } : undefined;
        },
        () => {
          setDeletedSvgIds([...deletedSvgIds, confirmDeleteSvgId!]);
          setConfirmDeleteSvgId(null);
        },
        () => setConfirmDeleteSvgId(null),
      )}
      {renderDeleteConfirmDialog(
        confirmDeletePendingIndex,
        () => {
          const svg = pendingSvgs[confirmDeletePendingIndex!];
          return svg ? { svg } : undefined;
        },
        () => {
          setPendingSvgs(pendingSvgs.filter((_, i) => i !== confirmDeletePendingIndex));
          setConfirmDeletePendingIndex(null);
        },
        () => setConfirmDeletePendingIndex(null),
      )}

      {/* 既存SymbolSVG名前編集ダイアログ */}
      <SvgNameInputDialog
        open={editingSymbolSvgId !== null}
        editMode={true}
        svgItems={(() => {
          const targetSvg = existingSymbolSvgs.find(s => s.id === editingSymbolSvgId);
          return targetSvg ? [{ fileName: targetSvg.name, svg: targetSvg.svg }] : [];
        })()}
        onConfirm={(items) => {
          if (items.length > 0) {
            setExistingSymbolSvgs(existingSymbolSvgs.map(s =>
              s.id === editingSymbolSvgId ? { ...s, name: items[0].name } : s
            ));
          }
          setEditingSymbolSvgId(null);
        }}
        onCancel={() => setEditingSymbolSvgId(null)}
      />

      {/* 新規追加予定SymbolSVG名前編集ダイアログ */}
      <SvgNameInputDialog
        open={editingPendingSymbolIndex !== null}
        editMode={true}
        svgItems={(() => {
          const targetSvg = editingPendingSymbolIndex !== null ? pendingSymbolSvgs[editingPendingSymbolIndex] : undefined;
          return targetSvg ? [{ fileName: targetSvg.name, svg: targetSvg.svg }] : [];
        })()}
        onConfirm={(items) => {
          if (items.length > 0) {
            setPendingSymbolSvgs(pendingSymbolSvgs.map((s, i) =>
              i === editingPendingSymbolIndex ? { ...s, name: items[0].name } : s
            ));
          }
          setEditingPendingSymbolIndex(null);
        }}
        onCancel={() => setEditingPendingSymbolIndex(null)}
      />

      {/* デフォルトシンボル警告ダイアログ */}
      <NotifyDialog
        open={showDefaultSymbolWarning}
        title=""
        message={<p>デフォルトシンボルは変更できません</p>}
        onYes={() => setShowDefaultSymbolWarning(false)}
      />

      {/* クライアント未選択警告ダイアログ */}
      <NotifyDialog
        open={showCustomerSelectWarning}
        title=""
        message={<p>クライアントを選択してください</p>}
        onYes={() => setShowCustomerSelectWarning(false)}
      />
    </div>
  );
};
